import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Construction, Loader2 } from 'lucide-react';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

interface PlaceholderPageProps {
  title: string;
  description?: string;
}

const PlaceholderPage: React.FC<PlaceholderPageProps> = ({ title, description }) => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Body scroll lock when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  // Redirect if not authenticated (only after loading completes)
  React.useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#AD0000] animate-spin" />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-red-600/20 via-gray-500/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-gray-600/20 via-gray-500/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-[#AD0000]/10 to-gray-700/20 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />

      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Content - Account for fixed nav and sidebar */}
      <div className="relative z-10 px-4 md:px-8 py-8 pt-40 md:pt-44 lg:ml-[240px] transition-all duration-300">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-black mb-2">{title}</h1>
            {description && (
              <p className="text-slate-400">{description}</p>
            )}
          </div>

          {/* Coming Soon Card */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#AD0000]/10 to-gray-700/20 rounded-2xl blur-xl"></div>

            <div className="relative bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
              <div className="p-12 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-[#AD0000]/10 to-gray-700/20 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <Construction className="w-12 h-12 text-gray-300" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">Coming Soon</h2>
                <p className="text-slate-400 max-w-md mx-auto">
                  This feature is currently under development. Check back soon for updates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PlaceholderPage;
